import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { api, scriptClass, langLabel } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Mic, StopCircle, Type as TypeIcon, ChevronRight, Search } from "lucide-react";
import { toast } from "sonner";

/* Step-based new complaint flow:
 * 1) intake   — voice/type
 * 2) confirm  — extracted structured fields + entity picker (auto after creating complaint)
 * 3) review   — AI draft, attestation
 * 4) send     — Gmail/mailto/copy compose
 */

export default function NewComplaint() {
    const [step, setStep] = useState("intake");
    const [working, setWorking] = useState(""); // working_text_en
    const [native, setNative] = useState("");
    const [lang, setLang] = useState(null);
    const [entityId, setEntityId] = useState(null);
    const [typed, setTyped] = useState(false);
    const [complaint, setComplaint] = useState(null);
    const [composing, setComposing] = useState(null); // approve response
    const [busy, setBusy] = useState(false);

    const goConfirm = async () => {
        if (!working.trim() || working.trim().length < 12) {
            toast.error("Please add a bit more detail about what happened.");
            return;
        }
        setBusy(true);
        try {
            const r = await api.post("/complaints", {
                working_text: working,
                entity_id: entityId || null,
                typed,
            });
            setComplaint(r.data.complaint);
            setStep("confirm");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not draft your complaint");
        } finally {
            setBusy(false);
        }
    };

    return (
        <Layout>
            <Stepper step={step} />
            {step === "intake" && (
                <IntakeStep
                    working={working}
                    setWorking={setWorking}
                    native={native}
                    setNative={setNative}
                    lang={lang}
                    setLang={setLang}
                    typed={typed}
                    setTyped={setTyped}
                    onNext={goConfirm}
                    busy={busy}
                />
            )}
            {step === "confirm" && complaint && (
                <ConfirmStep
                    complaint={complaint}
                    onUpdated={(c) => setComplaint(c)}
                    onNext={() => setStep("review")}
                />
            )}
            {step === "review" && complaint && (
                <ReviewStep
                    complaint={complaint}
                    onApprove={(resp) => {
                        setComposing(resp);
                        setStep("send");
                    }}
                />
            )}
            {step === "send" && composing && complaint && <SendStep composing={composing} complaint={complaint} />}
        </Layout>
    );
}

function Stepper({ step }) {
    const steps = [
        ["intake", "Tell us"],
        ["confirm", "Confirm"],
        ["review", "Review"],
        ["send", "Send"],
    ];
    const idx = steps.findIndex(([k]) => k === step);
    return (
        <ol className="flex items-center gap-2 mb-6 text-[12px] tabular" data-testid="new-stepper">
            {steps.map(([k, label], i) => (
                <li key={k} className="flex items-center gap-2">
                    <span
                        className={`inline-flex h-7 min-w-7 px-2 items-center justify-center rounded-full font-semibold ${
                            i <= idx ? "text-white" : "text-[var(--speakup-text-muted)] bg-[#f3f4f6]"
                        }`}
                        style={i <= idx ? { background: "var(--speakup-red)" } : {}}
                    >
                        {i + 1}
                    </span>
                    <span className={i === idx ? "text-[var(--speakup-text-strong)] font-semibold" : "text-[var(--speakup-text-muted)]"}>{label}</span>
                    {i < steps.length - 1 && <ChevronRight size={14} className="text-[var(--speakup-text-muted)]" />}
                </li>
            ))}
        </ol>
    );
}

function IntakeStep({ working, setWorking, native, setNative, lang, setLang, typed, setTyped, onNext, busy }) {
    const [recording, setRecording] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [mode, setMode] = useState("voice"); // voice | type
    const recRef = useRef(null);
    const chunksRef = useRef([]);

    const startRecording = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const mr = new MediaRecorder(stream, { mimeType: "audio/webm" });
            recRef.current = mr;
            chunksRef.current = [];
            mr.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
            mr.onstop = async () => {
                stream.getTracks().forEach((t) => t.stop());
                const blob = new Blob(chunksRef.current, { type: "audio/webm" });
                if (blob.size < 1000) {
                    toast.error("Recording too short. Hold for at least a couple of seconds.");
                    return;
                }
                setUploading(true);
                const fd = new FormData();
                fd.append("audio", blob, "audio.webm");
                try {
                    const r = await api.post("/intake/voice", fd, {
                        headers: { "Content-Type": "multipart/form-data" },
                    });
                    setWorking(r.data.working_text_en || "");
                    setNative(r.data.native_transcript || "");
                    setLang({ code: r.data.language_code, prob: r.data.language_probability });
                    setTyped(false);
                } catch (e) {
                    toast.error(e?.response?.data?.detail || "Could not transcribe. Try again or type instead.");
                } finally {
                    setUploading(false);
                }
            };
            mr.start();
            setRecording(true);
        } catch (e) {
            toast.error("Microphone permission needed. Try 'Type instead' below.");
        }
    };

    const stopRecording = () => {
        recRef.current?.stop();
        setRecording(false);
    };

    return (
        <div className="speakup-card p-6 md:p-8" data-testid="intake-step">
            <h1 className="display text-[26px] md:text-[30px]">Tell us what happened</h1>
            <p className="text-[var(--speakup-text-muted)] mt-2">
                Speak in your own language — Hindi, English, Tamil and more. Mention the bank/NBFC, the issue, the
                amount, dates and your card/account reference if you can.
            </p>

            <div className="mt-4 flex gap-2 text-sm">
                <button
                    type="button"
                    onClick={() => setMode("voice")}
                    data-testid="intake-mode-voice"
                    className={`h-9 px-3 rounded-full border ${mode === "voice" ? "bg-[var(--speakup-text-strong)] text-white border-transparent" : "border-[var(--speakup-border)]"}`}
                >
                    Speak
                </button>
                <button
                    type="button"
                    onClick={() => setMode("type")}
                    data-testid="intake-mode-type"
                    className={`h-9 px-3 rounded-full border ${mode === "type" ? "bg-[var(--speakup-text-strong)] text-white border-transparent" : "border-[var(--speakup-border)]"}`}
                >
                    <span className="inline-flex items-center gap-1.5">
                        <TypeIcon size={14} /> Type instead
                    </span>
                </button>
            </div>

            {mode === "voice" && (
                <div className="mt-8 flex flex-col items-center" data-testid="intake-voice-block">
                    <button
                        onClick={recording ? stopRecording : startRecording}
                        disabled={uploading}
                        data-testid="intake-mic-btn"
                        className={`relative h-32 w-32 rounded-full text-white flex items-center justify-center transition-transform active:scale-95 ${recording ? "speakup-mic-pulse" : ""}`}
                        style={{ background: "var(--speakup-gradient)" }}
                        aria-label={recording ? "Stop recording" : "Start recording"}
                    >
                        {recording ? <StopCircle size={44} /> : <Mic size={44} />}
                    </button>
                    <p className="mt-4 text-[14px] text-[var(--speakup-text-muted)]">
                        {uploading
                            ? "Transcribing your voice…"
                            : recording
                              ? "Listening. Tap again to stop."
                              : "Tap and speak. Hold for at least 3–5 seconds."}
                    </p>
                </div>
            )}

            {mode === "type" && (
                <div className="mt-6">
                    <textarea
                        data-testid="intake-typed-input"
                        value={working}
                        onChange={(e) => {
                            setWorking(e.target.value);
                            setTyped(true);
                            setLang(null);
                            setNative("");
                        }}
                        rows={6}
                        placeholder="e.g. HDFC charged me ₹4,200 wrongly on my credit card ending 4471 on 12 Jan. I want it reversed."
                        className="w-full p-4 rounded-[12px] border border-[var(--speakup-border)] text-[15px] focus:outline-none focus:ring-2 focus:ring-[var(--speakup-red)]/40"
                    />
                </div>
            )}

            {/* Transcript confirmation */}
            {(working || native) && (
                <div className="mt-6" data-testid="intake-transcripts">
                    {native && native !== working && (
                        <div className="mb-4">
                            <div className="text-[12px] uppercase tracking-wider text-[var(--speakup-text-muted)] mb-1">
                                Here's what I heard {lang?.code ? `· ${langLabel(lang.code)}` : ""}
                            </div>
                            <div
                                className={`speakup-card p-4 text-[15px] ${scriptClass(native)}`}
                                lang={lang?.code}
                                data-testid="intake-native-transcript"
                            >
                                {native}
                            </div>
                        </div>
                    )}
                    <div>
                        <div className="text-[12px] uppercase tracking-wider text-[var(--speakup-text-muted)] mb-1">
                            Working text (English) — edit anything that's off
                        </div>
                        <textarea
                            value={working}
                            onChange={(e) => setWorking(e.target.value)}
                            rows={5}
                            data-testid="intake-working-text"
                            className="w-full p-4 rounded-[12px] border border-[var(--speakup-border)] text-[15px] focus:outline-none focus:ring-2 focus:ring-[var(--speakup-red)]/40"
                        />
                    </div>
                </div>
            )}

            <div className="mt-6 flex justify-end">
                <button
                    onClick={onNext}
                    disabled={busy || !working.trim()}
                    data-testid="intake-continue-btn"
                    className="h-12 px-6 rounded-[12px] text-white font-semibold disabled:opacity-60"
                    style={{ background: "var(--speakup-red)" }}
                >
                    {busy ? "Drafting…" : "Continue"}
                </button>
            </div>
        </div>
    );
}

function ConfirmStep({ complaint, onUpdated, onNext }) {
    const { consumer } = useAuth();
    const [sp, setSp] = useState(complaint.structured_payload || {});
    const [entityId, setEntityId] = useState(complaint.entity_id || null);
    const [entityName, setEntityName] = useState(complaint.entity_name_extracted || "");
    const [contactPhone, setContactPhone] = useState(complaint.contact_phone || consumer?.phone || "");
    const [saving, setSaving] = useState(false);

    const save = async () => {
        setSaving(true);
        try {
            const phoneChanged = (contactPhone || "") !== (complaint.contact_phone || consumer?.phone || "");
            await api.patch(`/complaints/${complaint.id}`, {
                structured_payload: sp,
                entity_id: entityId || null,
                contact_phone: contactPhone || null,
                redraft: phoneChanged, // regenerate signature if user changed phone
            });
            // Re-fetch complaint
            const r = await api.get(`/complaints/${complaint.id}`);
            onUpdated(r.data.complaint);
            onNext();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not save");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="speakup-card p-6 md:p-8" data-testid="confirm-step">
            <h1 className="display text-[26px] md:text-[30px]">Confirm the details</h1>
            <p className="text-[var(--speakup-text-muted)] mt-2">
                Everything here is editable. Accurate facts mean a stronger complaint.
            </p>

            <div className="mt-6 space-y-5">
                <EntityPicker
                    valueId={entityId}
                    valueName={entityName}
                    onChange={(id, name) => {
                        setEntityId(id);
                        setEntityName(name);
                    }}
                />

                <Field label="Issue category">
                    <select
                        value={sp.issue_category || "other"}
                        onChange={(e) => setSp({ ...sp, issue_category: e.target.value })}
                        data-testid="confirm-issue-category"
                        className="w-full h-11 px-3 rounded-[10px] border border-[var(--speakup-border)] bg-white"
                    >
                        {[
                            "wrong/excess charge",
                            "failed transaction",
                            "unauthorized debit",
                            "loan/EMI issue",
                            "card issue",
                            "mis-selling",
                            "service deficiency",
                            "other",
                        ].map((v) => (
                            <option key={v} value={v}>
                                {v}
                            </option>
                        ))}
                    </select>
                </Field>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Field label="Amount disputed (₹)">
                        <input
                            type="number"
                            value={sp.amount_disputed ?? ""}
                            onChange={(e) =>
                                setSp({ ...sp, amount_disputed: e.target.value ? Number(e.target.value) : null })
                            }
                            data-testid="confirm-amount"
                            className="w-full h-11 px-3 rounded-[10px] border border-[var(--speakup-border)] tabular"
                        />
                    </Field>
                    <Field label="Event date">
                        <input
                            type="date"
                            value={(sp.event_date || "").slice(0, 10)}
                            onChange={(e) => setSp({ ...sp, event_date: e.target.value || null })}
                            data-testid="confirm-event-date"
                            className="w-full h-11 px-3 rounded-[10px] border border-[var(--speakup-border)] tabular"
                        />
                    </Field>
                </div>

                <Field label="Account / card / loan reference (mask if needed)">
                    <input
                        value={sp.account_or_policy_ref || ""}
                        onChange={(e) => setSp({ ...sp, account_or_policy_ref: e.target.value || null })}
                        placeholder="e.g. Card ending 4471"
                        data-testid="confirm-account-ref"
                        className="w-full h-11 px-3 rounded-[10px] border border-[var(--speakup-border)] tabular"
                    />
                </Field>

                <Field label="Registered mobile number (so the bank can reach you)">
                    <input
                        value={contactPhone}
                        onChange={(e) => setContactPhone(e.target.value)}
                        placeholder="+91 …"
                        data-testid="confirm-contact-phone"
                        inputMode="tel"
                        className="w-full h-11 px-3 rounded-[10px] border border-[var(--speakup-border)] tabular"
                    />
                    <div className="text-[12px] text-[var(--speakup-text-muted)] mt-1">
                        Pre-filled from your sign-in number. Change if your bank knows you under a different one — we'll include it in the email.
                    </div>
                </Field>

                <Field label="What relief are you seeking?">
                    <textarea
                        rows={3}
                        value={sp.relief_sought || ""}
                        onChange={(e) => setSp({ ...sp, relief_sought: e.target.value })}
                        data-testid="confirm-relief"
                        className="w-full p-3 rounded-[10px] border border-[var(--speakup-border)]"
                    />
                </Field>

                <Field label="Short summary (≤2 sentences)">
                    <textarea
                        rows={2}
                        value={sp.summary || ""}
                        onChange={(e) => setSp({ ...sp, summary: e.target.value })}
                        data-testid="confirm-summary"
                        className="w-full p-3 rounded-[10px] border border-[var(--speakup-border)]"
                    />
                </Field>
            </div>

            <div className="mt-6 flex items-center justify-between">
                {!entityId && (
                    <p className="text-[13px] text-[var(--speakup-waiting)]">
                        Pick a recognised institution to enable sending.
                    </p>
                )}
                <button
                    onClick={save}
                    disabled={!entityId || saving}
                    data-testid="confirm-continue-btn"
                    className="ml-auto h-12 px-6 rounded-[12px] text-white font-semibold disabled:opacity-60"
                    style={{ background: "var(--speakup-red)" }}
                >
                    {saving ? "Saving…" : "Continue to draft"}
                </button>
            </div>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <label className="block">
            <div className="text-[13px] font-semibold text-[var(--speakup-text-strong)] mb-1.5">{label}</div>
            {children}
        </label>
    );
}

function EntityPicker({ valueId, valueName, onChange }) {
    const [q, setQ] = useState(valueName || "");
    const [items, setItems] = useState([]);
    const [allItems, setAllItems] = useState([]); // unfiltered, for chips
    const [showList, setShowList] = useState(false);
    const [requesting, setRequesting] = useState(false);

    useEffect(() => {
        api.get(`/entities`).then((r) => setAllItems(r.data.items || []));
    }, []);

    useEffect(() => {
        const h = setTimeout(() => {
            api.get(`/entities${q ? `?q=${encodeURIComponent(q)}` : ""}`).then((r) => setItems(r.data.items));
        }, 200);
        return () => clearTimeout(h);
    }, [q]);

    const pick = (it) => {
        onChange(it.id, it.name);
        setQ(it.name);
        setShowList(false);
    };

    const requestAdd = async () => {
        if (!q.trim()) return;
        setRequesting(true);
        try {
            await api.post("/entities/request", { name: q, note: null });
            toast.success("Thanks — we'll add this institution shortly.");
        } finally {
            setRequesting(false);
        }
    };

    return (
        <Field label="Institution">
            <div className="relative">
                <div className="flex items-center gap-2">
                    <div className="relative flex-1">
                        <Search
                            size={16}
                            className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--speakup-text-muted)]"
                        />
                        <input
                            value={q}
                            onChange={(e) => {
                                setQ(e.target.value);
                                onChange(null, e.target.value);
                                setShowList(true);
                            }}
                            onFocus={() => setShowList(true)}
                            placeholder="Type bank/NBFC name…"
                            data-testid="confirm-entity-input"
                            className="w-full h-11 pl-9 pr-3 rounded-[10px] border border-[var(--speakup-border)]"
                        />
                    </div>
                    {valueId && (
                        <span className="chip chip-progress" data-testid="confirm-entity-selected">
                            Selected
                        </span>
                    )}
                </div>
                {showList && (
                    <div
                        className="speakup-card absolute z-10 left-0 right-0 mt-1 max-h-64 overflow-auto"
                        data-testid="confirm-entity-list"
                    >
                        {items.map((it) => (
                            <button
                                type="button"
                                key={it.id}
                                onClick={() => pick(it)}
                                className="w-full text-left px-4 py-3 hover:bg-[var(--speakup-surface-muted)] border-b last:border-b-0 border-[var(--speakup-border)]"
                                data-testid={`confirm-entity-option-${it.id}`}
                            >
                                <div className="font-semibold">{it.name}</div>
                                <div className="text-[12px] text-[var(--speakup-text-muted)] uppercase">{it.type}</div>
                            </button>
                        ))}
                        {items.length === 0 && (
                            <div className="px-4 py-3 text-sm text-[var(--speakup-text-muted)]">
                                No match. <button onClick={requestAdd} className="underline" data-testid="confirm-request-entity" disabled={requesting}>Request we add "{q}"</button>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Discoverability: chips of supported institutions */}
            {allItems.length > 0 && (
                <div className="mt-3" data-testid="confirm-entity-chips-wrap">
                    <div className="text-[11px] uppercase tracking-wider text-[var(--speakup-text-muted)] mb-1.5">
                        Supported now ({allItems.length}) — tap to pick
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {allItems.map((it) => {
                            const active = it.id === valueId;
                            return (
                                <button
                                    type="button"
                                    key={it.id}
                                    onClick={() => pick(it)}
                                    data-testid={`confirm-entity-chip-${it.id}`}
                                    className={`h-9 px-3 rounded-full text-[13px] font-semibold border transition-colors ${
                                        active
                                            ? "text-white border-transparent"
                                            : "bg-white border-[var(--speakup-border)] text-[var(--speakup-text-strong)] hover:bg-[var(--speakup-surface-muted)]"
                                    }`}
                                    style={active ? { background: "var(--speakup-red)" } : {}}
                                >
                                    {it.name}
                                </button>
                            );
                        })}
                    </div>
                    <div className="text-[11px] text-[var(--speakup-text-muted)] mt-2">
                        Don't see yours? Type the name above and tap "Request we add it" — we'll do it.
                    </div>
                </div>
            )}
        </Field>
    );
}

function ReviewStep({ complaint, onApprove }) {
    const [body, setBody] = useState(complaint.draft_body || "");
    const [subject, setSubject] = useState(complaint.draft_subject || "");
    const [attested, setAttested] = useState(false);
    const [busy, setBusy] = useState(false);
    const [translated, setTranslated] = useState(null);
    const [translateLang, setTranslateLang] = useState(null);

    const translate = async (lang) => {
        setTranslateLang(lang);
        try {
            const r = await api.post(`/complaints/${complaint.id}/translate-draft`, { target_lang: lang });
            setTranslated(r.data.text);
        } catch {
            toast.error("Translation failed");
        }
    };

    const approve = async () => {
        if (!attested) return;
        setBusy(true);
        try {
            // Save edits first
            await api.patch(`/complaints/${complaint.id}`, {
                draft_subject: subject,
                draft_body: body,
            });
            const r = await api.post(`/complaints/${complaint.id}/approve`, { attested: true });
            onApprove(r.data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not approve");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="speakup-card p-6 md:p-8" data-testid="review-step">
            <h1 className="display text-[26px] md:text-[30px]">Review your draft</h1>
            <p className="text-[var(--speakup-text-muted)] mt-2">
                This is the formal complaint we'll prefill into your email app. Edit freely.
            </p>

            <div className="mt-6">
                <div className="text-[12px] uppercase tracking-wider text-[var(--speakup-text-muted)] mb-1">Subject</div>
                <input
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    data-testid="review-subject"
                    className="w-full h-11 px-3 rounded-[10px] border border-[var(--speakup-border)] font-semibold"
                />
            </div>

            <div className="mt-4">
                <div className="text-[12px] uppercase tracking-wider text-[var(--speakup-text-muted)] mb-1">Body</div>
                <textarea
                    rows={14}
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    data-testid="review-body"
                    className="w-full p-4 rounded-[10px] border border-[var(--speakup-border)] leading-relaxed text-[15px] focus:outline-none focus:ring-2 focus:ring-[var(--speakup-red)]/40"
                />
            </div>

            <div className="mt-3 flex gap-2 flex-wrap">
                <button
                    onClick={() => translate("hi-IN")}
                    data-testid="review-translate-hi"
                    className="h-9 px-3 rounded-full border border-[var(--speakup-border)] text-sm"
                >
                    View in Hindi
                </button>
                <button
                    onClick={() => translate("ta-IN")}
                    data-testid="review-translate-ta"
                    className="h-9 px-3 rounded-full border border-[var(--speakup-border)] text-sm"
                >
                    View in Tamil
                </button>
                {translated && (
                    <button
                        onClick={() => setTranslated(null)}
                        className="h-9 px-3 rounded-full text-sm text-[var(--speakup-text-muted)]"
                    >
                        Hide
                    </button>
                )}
            </div>

            {translated && (
                <div
                    className={`mt-3 speakup-card p-4 text-[15px] whitespace-pre-wrap ${scriptClass(translated)}`}
                    lang={translateLang}
                    data-testid="review-translated"
                >
                    {translated}
                </div>
            )}

            <label className="flex items-start gap-3 mt-6 cursor-pointer select-none">
                <input
                    type="checkbox"
                    checked={attested}
                    onChange={(e) => setAttested(e.target.checked)}
                    className="mt-1 h-5 w-5 accent-[var(--speakup-red)]"
                    data-testid="review-attest-checkbox"
                />
                <span className="text-[15px]">
                    <strong>I confirm these facts are true</strong> to the best of my knowledge. I understand SpeakUp
                    helps me draft and track; the complaint is mine.
                </span>
            </label>

            <div className="mt-6 flex justify-end">
                <button
                    onClick={approve}
                    disabled={!attested || busy}
                    data-testid="review-approve-btn"
                    className="h-12 px-6 rounded-[12px] text-white font-semibold disabled:opacity-60"
                    style={{ background: "var(--speakup-red)" }}
                >
                    {busy ? "Preparing…" : "Open in my email"}
                </button>
            </div>
        </div>
    );
}

function SendStep({ composing, complaint }) {
    const nav = useNavigate();
    const [copied, setCopied] = useState(false);

    const copyAll = async () => {
        const text = `To: ${composing.recipient}\nSubject: ${composing.subject}\n\n${composing.body}`;
        try {
            await navigator.clipboard.writeText(text);
            setCopied(true);
            toast.success("Complaint copied — paste into your email.");
            setTimeout(() => setCopied(false), 2500);
        } catch {
            toast.error("Could not copy — please select and copy manually.");
        }
    };

    return (
        <div className="speakup-card p-6 md:p-8" data-testid="send-step">
            <h1 className="display text-[26px] md:text-[30px]">Send it from your email</h1>
            <p className="text-[var(--speakup-text-muted)] mt-2">
                We never send to the bank ourselves. The button below opens your email app with the complaint already
                written. A hidden copy comes back to us — that's how we know it's sent.
            </p>

            <div className="mt-6 grid gap-3 text-[14px]">
                <Row label="To" value={composing.recipient} testId="send-recipient" />
                <Row label="Subject" value={composing.subject} testId="send-subject" />
                <Row label="Hidden BCC (tracking)" value={composing.bcc_alias} mono testId="send-bcc" />
            </div>

            <details className="mt-4">
                <summary className="cursor-pointer text-[14px] font-semibold">Preview body</summary>
                <pre
                    data-testid="send-body-preview"
                    className="mt-2 p-4 bg-[var(--speakup-surface-muted)] rounded-[10px] text-[13px] whitespace-pre-wrap leading-relaxed"
                >
                    {composing.body}
                </pre>
            </details>

            <div className="mt-6 grid md:grid-cols-3 gap-3">
                <a
                    href={composing.gmail_url}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="send-open-gmail-btn"
                    className="h-12 inline-flex items-center justify-center rounded-[12px] text-white font-semibold"
                    style={{ background: "var(--speakup-red)" }}
                >
                    Open Gmail
                </a>
                <a
                    href={composing.mailto_url}
                    data-testid="send-open-mail-btn"
                    className="h-12 inline-flex items-center justify-center rounded-[12px] border-2 font-semibold"
                    style={{ borderColor: "var(--speakup-red)", color: "var(--speakup-red)" }}
                >
                    Open my email app
                </a>
                <button
                    onClick={copyAll}
                    data-testid="send-copy-btn"
                    className="h-12 inline-flex items-center justify-center rounded-[12px] border border-[var(--speakup-border)] font-semibold"
                >
                    {copied ? "Copied ✓" : "Copy complaint"}
                </button>
            </div>

            <div className="mt-7 pt-6 border-t border-[var(--speakup-border)]">
                <p className="text-[14px] text-[var(--speakup-text-muted)]">
                    Once you've sent it, head to the complaint timeline. If our tracking copy doesn't arrive in 24
                    hours, we'll ask you to confirm.
                </p>
                <button
                    onClick={() => nav(`/c/${complaint.id}`)}
                    data-testid="send-goto-timeline-btn"
                    className="mt-4 h-12 px-5 rounded-[12px] border border-[var(--speakup-text-strong)] font-semibold"
                >
                    Open my complaint timeline
                </button>
            </div>
        </div>
    );
}

function Row({ label, value, mono, testId }) {
    return (
        <div className="flex items-baseline gap-3">
            <div className="w-40 shrink-0 text-[12px] uppercase tracking-wider text-[var(--speakup-text-muted)]">
                {label}
            </div>
            <div
                className={`flex-1 ${mono ? "tabular" : ""} text-[var(--speakup-text-strong)] break-all`}
                data-testid={testId}
            >
                {value}
            </div>
        </div>
    );
}
